import { Card, CardContent, Tooltip } from "@altimateai/lego";
import { Button } from "@altimateai/lego";
import styles from "./styles.module.scss";
import { Paginate } from "../../components/paginate";
import dayjs from "dayjs";
import classnames from "classnames";
import { getScopeString } from "@/helpers/utils";
import { getIconByDatastoreType } from "./DatastoreImage";
import { CopyIconButton } from "../../components/copyButton";
import { ColorTag } from "../../components/tags";
import { CollapsibleField } from "../../components/text";
import { DataSourceType } from "@/types/datasource";
import ColumnTags from "./components/columnTags";
import { Stack } from "@altimateai/lego";
import { RelativeComponentLoader } from "@/components/loader";
import { TableItem as TableItemType } from "./types";
import { ComponentProps } from "react";

const colors = ["blue", "orange", "yellow", "purple", "dark_green"];
const colorsMap: Record<string, string> = {};
let i = 0;

interface SelectConfig {
  label: string;
  outline?: boolean;
  onSelect: (table: TableItemType) => void;
}

interface TableItemProps {
  table: TableItemType;
  onItemClick?: (table: TableItemType) => void;
  onDetailsClick?: (table: TableItemType) => void;
  select?: SelectConfig;
}

const TableItem = ({ table, onItemClick, onDetailsClick, select }: TableItemProps): JSX.Element => {
  const matchStrs: string[] = [];
  if (table.matches?.table_name) matchStrs.push("Table name");
  if (table.matches?.table_purpose_match) matchStrs.push("Table description");
  if (table.matches && table.matches.column_names && table.matches.column_names > 0) {
    matchStrs.push(`${table.matches.column_names} column names`);
  }
  if (
    table.matches &&
    table.matches.column_description_matches &&
    table.matches.column_description_matches > 0
  ) {
    matchStrs.push(`${table.matches.column_description_matches} column descriptions`);
  }
  const matchStr = matchStrs.join(", ");

  const scope = getScopeString(table.scope);
  const columnHasTags = table.columns.some(c => c.tags.length > 0);

  return (
    <div
      className={styles.card}
      onClick={(e: React.MouseEvent<HTMLDivElement>) => {
        e.stopPropagation();
        onItemClick?.(table);
      }}
    >
      <div className="d-flex">
        {getIconByDatastoreType(table.datasource_type, "me-2 icon-md")}
        <div className="text-primary fs-5 text-overflow">{table.name}</div>
      </div>
      <div className="al-flex al-items-center mt-2 mb-2">
        <div className="me-2 text-overflow">{scope}</div>
        <CopyIconButton value={scope} colorClassName="al-text-gray-500" isLight />
      </div>
      <div className="al-flex mt-2 mb-2">
        <div className="me-2">Total Columns:</div>
        <div className="text-muted">{table.columns.length}</div>
      </div>
      {table.purpose && (
        <CollapsibleField label="Description" text={table.purpose} defaultLines={3} />
      )}
      {table.datasource_type !== DataSourceType.POSTGRES && table.last_violation_timestamp && (
        <div className="al-flex mt-2 mb-2">
          <div className="me-2">Last Validation:</div>
          <div>
            <span
              className={classnames(
                "text-underline me-2 cursor-pointer",
                table.total_violations === 0 ? "level-green" : "level-orange"
              )}
              onClick={(e: React.MouseEvent<HTMLSpanElement>) => {
                e.stopPropagation();
                onItemClick?.(table);
              }}
            >
              {table.total_violations} Incidents
            </span>
            <span className="text-muted">
              ({dayjs(table.last_violation_timestamp).format("DD MMM YYYY, hh:mm A")})
            </span>
          </div>
        </div>
      )}
      {table.datasource_type !== DataSourceType.POSTGRES &&
        (table.tags.length > 0 || columnHasTags) && (
          <Stack gap="xs" className="al-mt-2 al-flex-nowrap">
            <div className="me-2">Tags:</div>
            <Stack gap="xs">
              {table.tags.map(t => {
                if (!colorsMap[t.name]) colorsMap[t.name] = colors[i++ % colors.length];
                return (
                  <Tooltip title={t.description} key={t.name}>
                    <ColorTag color={colorsMap[t.name]} label={t.name} />
                  </Tooltip>
                );
              })}
              {columnHasTags && <ColumnTags columns={table.columns} />}
            </Stack>
          </Stack>
        )}
      <div className="mt-2 al-flex gap-sm al-items-center">
        {matchStr && (
          <div className="d-flex">
            <div className="me-2">Matches:</div>
            <div className="text-primary">{matchStr}</div>
          </div>
        )}
        <div className="spacer" />
        {select && (
          <Button
            variant={select.outline ? "outline" : "default"}
            size="sm"
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
              e.stopPropagation();
              select.onSelect(table);
            }}
          >
            {select.label}
          </Button>
        )}
        {onDetailsClick && (
          <Button
            size="sm"
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
              e.stopPropagation();
              onDetailsClick(table);
            }}
          >
            View Details
          </Button>
        )}
      </div>
    </div>
  );
};

interface NoSearchResultsProps {
  search: string;
}

const NoSearchResults = ({ search }: NoSearchResultsProps): JSX.Element => (
  <div className={styles.card}>
    <div className="fs-4 fw-semibold mb-2">
      No results found for : <span className="text-primary">{search}</span>
    </div>
    <div className="fs-5 mb-2">Search help</div>
    <ul className="text-muted mb-2">
      <li>Check your search for typos</li>
      <li>Use more specific search terms</li>
    </ul>
  </div>
);

interface TableListProps {
  search: string;
  tables?: TableItemType[];
  onItemClick?: (table: TableItemType) => void;
  paginate: ComponentProps<typeof Paginate>;
  onDetailsClick?: (table: TableItemType) => void;
  select?: SelectConfig;
  isLoading?: boolean;
}

export function TableList({
  search,
  tables = [],
  onItemClick,
  paginate,
  onDetailsClick,
  select,
  isLoading,
}: TableListProps): JSX.Element {
  return (
    <div className={styles.table_list}>
      <Card>
        <CardContent className="al-pt-4">
          {isLoading ? (
            <RelativeComponentLoader label={"Loading datasets..."} />
          ) : (
            <>
              <Stack justify={"between"} align={"center"}>
                <div className="al-text-muted-foreground al-text-sm">
                  {paginate.itemCount} results
                </div>
              </Stack>
              {search && paginate.itemCount === 0 ? (
                <NoSearchResults search={search} />
              ) : (
                tables.map(table => (
                  <TableItem
                    key={table.id}
                    table={table}
                    onItemClick={onItemClick}
                    onDetailsClick={onDetailsClick}
                    select={select}
                  />
                ))
              )}
              <Paginate {...paginate} />
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
